import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

const DEFAULT_OPTIONS = [10, 20, 50];

const ItemsPerPageDropdown = ({
  itemsPerPage,
  value,
  onChange,
  height = "36px",
  className,
  /** Compact control: numeric value + caret only (e.g. after a “Show” label). */
  displayMode = "default",
  /** When true, toggle shows only the number (menu options stay numeric). */
  numericLabelOnly = false,
  /** Page-size choices; defaults to [5, 10, 20, 50]. */
  options: optionsProp,
}) => {
  const { t } = useTranslation("translation");
  const options = optionsProp?.length ? optionsProp : DEFAULT_OPTIONS;
  const resolvedPageSize = itemsPerPage ?? value ?? options[0];
  const isCompact = displayMode === "compact" || numericLabelOnly;
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [menuStyle, setMenuStyle] = useState(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const itemRefs = useRef([]);

  const closeDropdown = useCallback(() => {
    setShowDropdown(false);
    setHighlightedIndex(-1);
    setMenuStyle(null);
  }, []);

  const computeMenuStyle = useCallback(() => {
    const trigger = buttonRef.current;
    if (!trigger) return null;

    const rect = trigger.getBoundingClientRect();
    const menuHeight =
      menuRef.current?.offsetHeight ||
      Math.min(280, options.length * 34 + 12);
    const gap = 4;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < menuHeight && spaceAbove >= spaceBelow;

    return {
      position: "fixed",
      left: rect.left,
      width: Math.max(rect.width, 56),
      minWidth: Math.max(rect.width, 56),
      zIndex: 9999,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + gap }
        : { top: rect.bottom + gap }),
    };
  }, [options.length]);

  const updateMenuPosition = useCallback(() => {
    const nextStyle = computeMenuStyle();
    if (nextStyle) setMenuStyle(nextStyle);
  }, [computeMenuStyle]);

  const openDropdown = useCallback(() => {
    const nextStyle = computeMenuStyle();
    if (nextStyle) setMenuStyle(nextStyle);
    setShowDropdown(true);
    setHighlightedIndex(0);
  }, [computeMenuStyle]);

  useLayoutEffect(() => {
    if (!showDropdown) return undefined;
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", closeDropdown, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", closeDropdown, true);
    };
  }, [showDropdown, closeDropdown, updateMenuPosition]);

  useEffect(() => {
    if (!showDropdown) return undefined;
    updateMenuPosition();
  }, [showDropdown, highlightedIndex, updateMenuPosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = dropdownRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inTrigger && !inMenu) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeDropdown]);

  useEffect(() => {
    if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex].scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  const handleKeyDown = (e) => {
    if (!showDropdown) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % options.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev - 1 + options.length) % options.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < options.length) {
        const selected = options[highlightedIndex];
        onChange(selected);
        closeDropdown();
      }
    } else if (e.key === "Escape") {
      closeDropdown();
    }
  };

  return (
    <>
      <style>{`
        .ippx-container {
          width: 100%;
          max-width: 220px;
        }

        .ippx-container.ippx-compact,
        .ippx-container.ippx-container--numeric {
          width: auto;
          max-width: none;
          min-width: 4rem;
          flex-shrink: 0;
        }

        .ippx-container.ippx-container--numeric {
          max-width: 64px;
        }

        .ippx-button.ippx-compact-btn,
        .ippx-container.ippx-container--numeric .ippx-button {
          width: auto;
          min-width: 3.75rem;
          justify-content: center;
          gap: 0.35rem;
          padding: 6px 10px;
        }

        .ippx-container.ippx-container--numeric .ippx-button {
          padding: 5px 6px;
          column-gap: 2px;
          justify-content: space-between;
        }

        .ippx-button {
          width: 100%;
          height: ${height};
          font-size: 14px;
          color: #0d2d3c;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06); /* soft inner shadow */
          transition: box-shadow 0.2s ease-in-out;  
           border: 1px solid #dcdcdc;
          background-color: #fff;
          border-radius: 8px;
          padding: 6px 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          transition: border-color 0.2s ease-in-out;
        }

        .ippx-button:focus {
          outline: 0;
  box-shadow: 0 0 0 0.10rem rgba(13, 110, 253, 0.25); /* Bootstrap ring */
        }

        .ippx-menu {
          background: #fff;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          max-height: min(280px, 70vh);
          overflow-y: auto;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
        }

        .ippx-item {
          padding: 6px 12px;
          cursor: pointer;
          font-size: 13px;
          color: #0d2d3c;
          border-radius: 4px;
          transition: background-color 0.15s;
        }

        .ippx-item:hover,
        .ippx-item.highlighted {
          background-color: #f3f4f6;
        }

        .ippx-item.active {
          background-color: #0d2d3c;
          color: #ffffff;
          font-weight: 500;
        }

        .ippx-icon {
          transition: transform 0.25s ease-in-out;
        }

        .ippx-rotate-up {
          transform: rotate(180deg);
        }
      `}</style>

      <div
        className={`d-flex align-items-center position-relative justify-content-start ${className || ""} ippx-container${
          displayMode === "compact" ? " ippx-compact" : ""
        }${numericLabelOnly ? " ippx-container--numeric" : ""}`}
        ref={dropdownRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <button
          ref={buttonRef}
          type="button"
          className={`ippx-button${displayMode === "compact" ? " ippx-compact-btn" : ""}`}
          aria-label={isCompact ? `${t("itemsperpage")}: ${resolvedPageSize}` : undefined}
          aria-expanded={showDropdown}
          onClick={() => {
            if (showDropdown) closeDropdown();
            else openDropdown();
          }}
        >
          <span>
            {isCompact
              ? resolvedPageSize
              : `${t("itemsperpage")}: ${resolvedPageSize}`}
          </span>
          <i
            className={`fas fa-angle-down ippx-icon ${showDropdown ? "ippx-rotate-up" : ""}`}
          ></i>
        </button>

        {showDropdown &&
          createPortal(
            <div ref={menuRef} className="ippx-menu" style={menuStyle || {}}>
              {options.map((value, index) => (
                <div
                  key={value}
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  className={`ippx-item${resolvedPageSize === value ? " active" : ""}${
                    highlightedIndex === index ? " highlighted" : ""
                  }`}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseLeave={() => setHighlightedIndex(-1)}
                  onClick={() => {
                    onChange(value);
                    closeDropdown();
                  }}
                >
                  {value}
                </div>
              ))}
            </div>,
            document.body
          )}
      </div>
    </>
  );
};

export default ItemsPerPageDropdown;
